import { useState, useEffect } from "react"
import { Grid, IconButton, Typography, TextField, CardHeader, CardContent, Stack, Card, Tooltip } from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import { CustomDataGrid, GridContainer, InfoText } from "components/common-styles"
import { rootStore, useStores } from "stores/root-store"
import { twoDecimalPlace } from "utils/app-helper"
import { formatStandardDate } from "utils/date-helper"
import { CommonConstant } from "shared/constants/common-constant"

const ProductInfo = ({ onEditClicked }) => {
  const { InventoryStore } = useStores()

  const productInfo = { ...InventoryStore.productInformation }

  const [selectedDimension, setSelectedDimension] = useState({})

  const columns = [
    {
      field: "warehouse",
      headerName: "Warehouse",
      minWidth: 200,
      flex: 1,
    },
    {
      field: "lot",
      headerName: "Lot",
      minWidth: 200,
      flex: 1,
    },
    {
      field: "on_hand_quantity",
      headerName: "On Hand Qty",
      type: "number",
      width: 200,
      renderCell: params => {
        return twoDecimalPlace(params.value)
      },
    },
    {
      field: "in_suspense_quantity",
      headerName: "In Suspense Qty",
      type: "number",
      width: 200,
      renderCell: params => {
        return twoDecimalPlace(params.value)
      },
    },
    {
      field: "expiration_date",
      headerName: "Expiration Date",
      width: 150,
      renderCell: params => {
        return formatStandardDate(params.value)
      },
    },
    {
      field: "inventory_status",
      headerName: "Inventory Status",
      width: 200,
    },
  ]

  const setDimensionData = dimension => {
    if (dimension?.cross_reference?.length > 0) {
      const cross_references = dimension?.cross_reference.filter(x => x.quantity_unit_of_measure == dimension.quantity_unit_of_measure && x.cross_reference).map(x => x.cross_reference)
      dimension.cross_reference_data = cross_references
    }
    setSelectedDimension(dimension || {})
  }

  useEffect(() => {
    if (productInfo?.dimensions?.length > 0) {
      const dimension = productInfo?.dimensions[0]
      setDimensionData(dimension)
    }
  }, [])

  const handleDimensionChanged = e => {
    const dimension = productInfo?.dimensions.find(x => x.quantity_unit_of_measure == e.target.value)
    setDimensionData(dimension)
  }

  const getLotControlledValue = sourceValue => {
    let result = ""
    if (sourceValue) {
      result = sourceValue.toLowerCase() === "n" ? "No" : "Yes"
    }
    return result
  }

  return (
    <Stack spacing={2}>
      <Card>
        <CardHeader
          title="Product Information"
          action={
            !InventoryStore.isArchivalSearch && (
              <Tooltip placement="top" title="Edit">
                <IconButton id="btnEdit" size="large" onClick={onEditClicked}>
                  <EditIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )
          }
        />
        <CardContent>
          <GridContainer container spacing={3}>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Description
              </Grid>
              <Grid item xs={8}>
                <InfoText id="product_description">{productInfo?.product_description}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Packing Class
              </Grid>
              <Grid item xs={8}>
                <InfoText id="packing_class">{productInfo?.packing_class}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Item Category 1
              </Grid>
              <Grid item xs={8}>
                <InfoText id="item_category1">{productInfo?.item_category1}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Item Category 2
              </Grid>
              <Grid item xs={8}>
                <InfoText id="item_category2">{productInfo?.item_category2}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Item Category 3
              </Grid>
              <Grid item xs={8}>
                <InfoText id="item_category3">{productInfo?.item_category3}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Country of Origin
              </Grid>
              <Grid item xs={8}>
                <InfoText id="country_of_origin">{rootStore.getTextByValue("COUNTRY", productInfo?.country_of_origin)}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Harmonized Code
              </Grid>
              <Grid item xs={8}>
                <InfoText id="harmonized_code">{productInfo?.harmonized_code}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Long Description
              </Grid>
              <Grid item xs={8}>
                <InfoText id="long_description">{productInfo?.long_description}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Lot Control
              </Grid>
              <Grid item xs={8}>
                <InfoText id="lot_controlled">{getLotControlledValue(productInfo?.lot_controlled)}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Serial Number Tracking
              </Grid>
              <Grid item xs={8}>
                <InfoText id="serial_tracked">{productInfo?.serial_tracked}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                UN Number
              </Grid>
              <Grid item xs={8}>
                <InfoText id="un_number">{productInfo?.un_number}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Dangerous Goods Weight
              </Grid>
              <Grid item xs={8}>
                <InfoText id="dg_volume">{twoDecimalPlace(productInfo?.dg_volume)}</InfoText>
              </Grid>
            </Grid>
          </GridContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title="Unit of Measure"
          action={
            productInfo?.dimensions?.length > 1 && (
              <TextField
                select
                id="quantity_unit_of_measure"
                style={{ width: 150 }}
                SelectProps={{
                  native: true,
                }}
                value={selectedDimension?.quantity_unit_of_measure}
                onChange={handleDimensionChanged}
              >
                {productInfo?.dimensions.map(option => (
                  <option key={option.quantity_unit_of_measure} value={option.quantity_unit_of_measure}>
                    {option.quantity_unit_of_measure}
                  </option>
                ))}
              </TextField>
            )
          }
        />
        <CardContent>
          <GridContainer container spacing={3}>
            <Grid item container md={6} xs={12}>
              <Grid item xs={5}>
                Length (M)
              </Grid>
              <Grid item xs={7}>
                <InfoText id="length">{selectedDimension?.length}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={5}>
                Width (M)
              </Grid>
              <Grid item xs={7}>
                <InfoText id="width">{selectedDimension?.width}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={5}>
                Height (M)
              </Grid>
              <Grid item xs={7}>
                <InfoText id="height">{selectedDimension?.height}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={5}>
                Dimension Unit of Measure
              </Grid>
              <Grid item xs={7}>
                <InfoText id="dimension_unit_of_measure">{selectedDimension?.dimension_unit_of_measure}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={5}>
                Weight (KG)
              </Grid>
              <Grid item xs={7}>
                <InfoText id="weight">{selectedDimension?.weight}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={5}>
                Weight Unit of Measure
              </Grid>
              <Grid item xs={7}>
                <InfoText id="weight_unit_of_measure">{selectedDimension?.weight_unit_of_measure}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={5}>
                Treat As Loose
              </Grid>
              <Grid item xs={7}>
                <InfoText id="treat_as_loose">{selectedDimension?.treat_as_loose}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={5}>
                Sequence
              </Grid>
              <Grid item xs={7}>
                <InfoText id="sequence">{twoDecimalPlace(selectedDimension?.sequence)}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={5}>
                Conversion Qty
              </Grid>
              <Grid item xs={7}>
                <InfoText id="conversion_qty">{twoDecimalPlace(selectedDimension?.conversion_qty)}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={5}>
                Protection Value
              </Grid>
              <Grid item xs={7}>
                <InfoText id="protection_value">{twoDecimalPlace(selectedDimension?.protection_value)}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              {selectedDimension?.cross_reference_data?.length > 0 && (
                <>
                  <Grid item xs={5}>
                    <Typography variant="h7">Cross Reference</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    {selectedDimension?.cross_reference_data.map((x, idx) => {
                      return (
                        <InfoText id={`crossf-${idx}`} key={x}>
                          {x}
                        </InfoText>
                      )
                    })}
                  </Grid>
                </>
              )}
            </Grid>
          </GridContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title="Inventory" />
        <CardContent>
          <GridContainer container spacing={3}>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                On Hold Qty
              </Grid>
              <Grid item xs={8}>
                <InfoText>{twoDecimalPlace(productInfo?.on_hold_quantity)}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                In Suspense Qty
              </Grid>
              <Grid item xs={8}>
                <InfoText>{twoDecimalPlace(productInfo?.in_suspense_quantity)}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                In Process Qty
              </Grid>
              <Grid item xs={8}>
                <InfoText>{twoDecimalPlace(productInfo?.in_process_quantity)}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Sales Demand Qty
              </Grid>
              <Grid item xs={8}>
                <InfoText>{twoDecimalPlace(productInfo?.sales_demand_quantity)}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Available To Sell
              </Grid>
              <Grid item xs={8}>
                <InfoText>{twoDecimalPlace(productInfo?.available_to_sell_quantity)}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                On Hand Qty
              </Grid>
              <Grid item xs={8}>
                <InfoText>{twoDecimalPlace(productInfo?.on_hand_quantity)}</InfoText>
              </Grid>
            </Grid>
          </GridContainer>
          <CustomDataGrid
            getRowId={row => row.id}
            rows={productInfo.inventory}
            columns={columns}
            initialState={{
              sorting: {
                sortModel: [{ field: "warehouse", sort: CommonConstant.ASCENDING }],
              },
              pagination: {
                paginationModel: {
                  pageSize: 15,
                },
              },
            }}
            pageSizeOptions={[10, 15, 20, 50]}
          />
        </CardContent>
      </Card>
    </Stack>
  )
}

export default ProductInfo
